import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import {
  So5FixtureEvent,
  ThresholdStreakTaskCurrentThresholdState,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Ineligible } from '@sorare/core/src/atoms/icons/Ineligible';
import { Reward } from '@sorare/core/src/atoms/icons/Reward';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { DiamondStatus } from '@sorare/core/src/components/missions/DiamondStatus';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { StreakProgress } from 'components/missions/Streaks/StreakProgress';
import { getComposeTeamRoute } from 'lib/so5';

import type { StreakTaskFooter_taskInterface } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  width: 100%;
  background-color: var(--c-black);
  border-radius: var(--double-unit);
  padding: var(--intermediate-unit);
  border: 1px solid var(--border-color);
`;
const GreyDisabledText = styled.span`
  color: var(--c-nd-600);
  text-decoration-line: line-through;
`;

const RightAlignedHorizontal = styled(Horizontal)`
  align-self: flex-start;

  @media ${laptopAndAbove} {
    align-self: flex-end;
  }
`;

const taskStatus: {
  [k: string]: ReactNode;
} = {
  [ThresholdStreakTaskCurrentThresholdState.CLAIMED]: (
    <LabelS color="var(--c-nd-600)" bold>
      <Horizontal as="span" gap={0.5}>
        <DiamondStatus status="checked" color="var(--c-green-300)" />
        <FormattedMessage
          id="StreakTaskFooter.claimed"
          defaultMessage="Claimed"
        />
      </Horizontal>
    </LabelS>
  ),
  [ThresholdStreakTaskCurrentThresholdState.UPCOMING_LINEUP_NOT_SET]: (
    <LabelS color="var(--c-nd-600)" bold>
      <FormattedMessage
        id="StreakTaskFooter.upcomingLineupNotSet"
        defaultMessage="Lineup Not Set"
      />
    </LabelS>
  ),
  [ThresholdStreakTaskCurrentThresholdState.UPCOMING_LINEUP_SET]: (
    <LabelS color="var(--c-nd-600)" bold>
      <FormattedMessage
        id="StreakTaskFooter.upcomingLineupSet"
        defaultMessage="Lineup Set"
      />
    </LabelS>
  ),
  [ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_IN_PROGRESS]: null,
  [ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_NOT_SET]: (
    <Horizontal>
      <Ineligible />
      <LabelS color="var(--c-nd-600)" bold>
        <FormattedMessage
          id="StreakTaskFooter.lineupNotSet"
          defaultMessage="Not Set"
        />
      </LabelS>
    </Horizontal>
  ),
  [ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_MISSED]: (
    <Horizontal gap={0.5}>
      <Ineligible />
      <LabelS color="var(--c-nd-600)" bold>
        <FormattedMessage
          id="StreakTaskFooter.lineupMissed"
          defaultMessage="Missed"
        />
      </LabelS>
    </Horizontal>
  ),
};

const endLabel = defineMessage({
  id: 'StreakTaskFooter.liveLineupHit.rewardReady',
  defaultMessage: 'Ready',
});

type Props = {
  streakTask: StreakTaskFooter_taskInterface;
};
export const StreakTaskFooter = ({ streakTask }: Props) => {
  const { sport } = useSportContext();
  const { formatMessage } = useIntl();

  if (!isType(streakTask, 'ThresholdsStreakTask')) {
    return null;
  }

  const {
    so5LeagueTrack,
    rewardConfigs,
    currentThresholdStatus,
    currentThreshold,
    rewardTaskCampaign,
    engaged,
  } = streakTask;

  const rewardConfig = rewardConfigs.at(0);

  const rewardNode = !!rewardConfig && (
    <AnyRewardConfig
      anyRewardConfig={rewardConfig}
      variant={Variant.INLINE}
      customText={
        !!currentThreshold.originalRewardConfigs &&
        !!rewardTaskCampaign &&
        (({ rewardText }) => (
          <Horizontal gap={0.5}>
            <span
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': `linear-gradient(323deg, ${rewardTaskCampaign.color1}, ${rewardTaskCampaign.color2})`,
                } as CSSProperties
              }
            >
              {rewardText}
            </span>
            {currentThreshold.originalRewardConfigs?.map(
              originalRewardConfig => (
                <GreyDisabledText key={originalRewardConfig.id}>
                  <RewardConfigText anyRewardConfig={originalRewardConfig} />
                </GreyDisabledText>
              )
            )}
          </Horizontal>
        ))
      }
    />
  );

  if (!engaged) {
    return (
      <RightAlignedHorizontal>
        {rewardNode}
        {!!so5LeagueTrack?.entrySo5Leaderboard && (
          <LinkOther
            as={Button}
            color="quaternary"
            size="small"
            to={getComposeTeamRoute({
              leaderboardSlug: so5LeagueTrack.entrySo5Leaderboard.slug,
              sport,
              eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
            })}
          >
            <FormattedMessage {...fantasy.setLineup} />
          </LinkOther>
        )}
      </RightAlignedHorizontal>
    );
  }

  return (
    <Root
      style={
        {
          '--border-color': so5LeagueTrack?.entrySo5Leaderboard.so5League.color,
        } as CSSProperties
      }
    >
      <SBHorizontal>
        <Horizontal>
          <LabelM bold>
            <FormattedMessage {...glossary.reward} />
          </LabelM>
          {currentThresholdStatus.state ===
            ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_HIT && (
            <Horizontal gap={0.5}>
              <Reward />
              {!!currentThresholdStatus.claimableAt && (
                <LabelS color="var(--c-rewards-orange)">
                  <TimeLeft
                    time={currentThresholdStatus.claimableAt}
                    endLabel={formatMessage(endLabel)}
                  />
                </LabelS>
              )}
            </Horizontal>
          )}
        </Horizontal>
        {rewardNode}
      </SBHorizontal>
      <StreakProgress managerTask={streakTask} />
      {taskStatus[currentThresholdStatus.state]}
    </Root>
  );
};

StreakTaskFooter.fragments = {
  taskInterface: gql`
    fragment StreakTaskFooter_taskInterface on TaskInterface {
      id
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ... on ThresholdsStreakTask {
        id
        engaged
        rewardTaskCampaign {
          slug
          color1: color(variant: PRIMARY)
          color2: color(variant: SECONDARY)
        }
        currentThreshold {
          originalRewardConfigs {
            id
            ...AnyRewardConfig_anyRewardConfigInterface
          }
        }
        currentThresholdStatus {
          state
          claimableAt
        }
        so5LeagueTrack {
          slug
          entrySo5Leaderboard {
            slug
            so5League {
              slug
              color(variant: PRIMARY)
            }
          }
        }
      }
      ...StreakProgress_managerTaskInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${StreakProgress.fragments.managerTask}
  ` as TypedDocumentNode<StreakTaskFooter_taskInterface>,
};
